import loadable from '@loadable/component';
import {
  IStoreLocatorAutocomplete,
  IStoreLocatorPlace,
} from '@models/IStoreLocatorAutocomplete';
import storeLocatorAutocomplete from '@services/storeLocatorAutocomplete';
import updateDataLayer from '@utils/helpers/gtm';
import classNames from 'classnames';
import { useState } from 'react';
import { AutosuggestProps } from 'react-autosuggest';

const Autosuggest = loadable<
  AutosuggestProps<IStoreLocatorAutocomplete, unknown>
>(
  () => import(/* webpackChunkName: "react-autosuggest" */ 'react-autosuggest')
);

interface Props {
  readonly autocompleteEndpoint: string;
  readonly className?: string;
  readonly countryCode: string;
  readonly dataTestId: string;
  readonly queryText: string;
  readonly searchPlaceholderLabel: string;
  readonly type: string;
  readonly onSearch: () => void;
  readonly onSetPlace: (place: IStoreLocatorPlace) => void;
  readonly onSetQueryText: (queryText: string) => void;
}

const StoreLocatorAutosuggest = ({
  autocompleteEndpoint,
  className,
  countryCode,
  dataTestId,
  queryText,
  searchPlaceholderLabel,
  type,
  onSearch,
  onSetPlace,
  onSetQueryText,
}: Props) => {
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IStoreLocatorAutocomplete[]
  >([]);

  const fetchAutoCompleteSuggestions = async ({
    value,
    reason,
  }: {
    value: string;
    reason: string;
  }) => {
    if (reason === 'input-changed') {
      if (value.length < 3) {
        if (autoCompleteSuggestions.length > 0) setAutoCompleteSuggestions([]);

        return;
      }

      try {
        const autoCompleteResponse = await storeLocatorAutocomplete({
          endpoint: autocompleteEndpoint,
          countryCode,
          searchTerm: value,
        });

        setAutoCompleteSuggestions(autoCompleteResponse);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: Allowed during error reporting
        console.log({ error });
      }
    }
  };

  return (
    <div
      className={classNames('autosuggest-wrapper', className)}
      data-testid={dataTestId}
    >
      <Autosuggest
        suggestions={autoCompleteSuggestions || []}
        onSuggestionsFetchRequested={fetchAutoCompleteSuggestions}
        onSuggestionsClearRequested={() => setAutoCompleteSuggestions([])}
        getSuggestionValue={(suggestion) => suggestion.description}
        renderSuggestion={(suggestion) => <span>{suggestion.description}</span>}
        onSuggestionSelected={(event, { suggestion }) => {
          event.stopPropagation();
          onSetPlace({
            id: suggestion.placeId,
            name: suggestion.description,
          });
          onSetQueryText(suggestion.description);
        }}
        inputProps={{
          value: queryText || '',
          onChange: (_, { newValue }) => {
            onSetQueryText(newValue);
          },
          onKeyUp: (event) => {
            if (event.key === 'Enter' && queryText.length >= 3) {
              onSearch();
              updateDataLayer({
                event: 'searchBar',
                search_term: queryText,
                content_type: `store_listing_${type}`,
                page_type: 'Store Locator',
              });
            }
          },
          placeholder: searchPlaceholderLabel,
        }}
      />
    </div>
  );
};

export default StoreLocatorAutosuggest;
